import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { Preloader } from "@/components";
import { useAdminChatOutlet } from "./AdminChatUserLayout";
import { useAdminChats } from "../model/AdminChatsContext";
import { useGetUserActionsMutation } from "@/store/usersApi";
import { formatScheduleDate } from "@/shared/utils";
import { ReactComponent as FilterIcon } from "@/assets/img/filter-icon.svg";

import styles from "./AdminChatLogsPage.module.scss";

export const AdminChatLogsPage = () => {
  const { userId, profile } = useAdminChatOutlet();
  const { initData } = useAdminChats();

  const [
    getUserActions,
    { data: actionsData, isLoading: isActionsLoading, isError: isActionsError },
  ] = useGetUserActionsMutation();

  const [isLoading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Оптимизированная фильтрация логов
  const filteredLogs = useMemo(() => {
    if (!actionsData?.actions || !Array.isArray(actionsData.actions)) {
      return [];
    }

    let filtered = [...actionsData.actions];

    // Фильтр по поисковому запросу
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((action: any) =>
        action.action?.toLowerCase().includes(query)
      );
    }

    // Фильтр по дате
    if (startDate || endDate) {
      filtered = filtered.filter((action: any) => {
        if (!action.of_date) return false;
        const actionDate = new Date(action.of_date);
        actionDate.setHours(0, 0, 0, 0);

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (actionDate < start) return false;
        }

        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (actionDate > end) return false;
        }

        return true;
      });
    }

    return filtered;
  }, [actionsData?.actions, searchQuery, startDate, endDate]);

  useEffect(() => {
    const loadLogs = async () => {
      if (!initData) {
        return;
      }

      setLoading(true);

      try {
        await getUserActions({
          user_id: userId,
        }).unwrap();
      } catch (error) {
        console.error("Ошибка при загрузке логов:", error);
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [initData, userId, getUserActions]);

  const handleClearFilters = useCallback(() => {
    setSearchQuery("");
    setStartDate(null);
    setEndDate(null);
  }, []);

  const hasActiveFilters = searchQuery.trim() || startDate || endDate;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <Link to=".." className={styles.back}>
          ← Назад к клиенту
        </Link>
        <h3>Логи пользователя</h3>
        <div className={styles.headerActions}>
          <button
            className={styles.filterButton}
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Фильтры"
          >
            <FilterIcon />
            {hasActiveFilters && <span className={styles.filterBadge} />}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className={styles.filtersPanel}>
          <div className={styles.searchInput}>
            <input
              type="text"
              placeholder="Поиск по логам..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchField}
            />
          </div>
          <div className={styles.dateFilters}>
            <div className={styles.dateFilter}>
              <label>С:</label>
              <DatePicker
                selected={startDate || undefined}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                maxDate={endDate || new Date()}
                dateFormat="dd.MM.yyyy"
                placeholderText="Начало"
                className={styles.datePicker}
                isClearable
              />
            </div>
            <div className={styles.dateFilter}>
              <label>По:</label>
              <DatePicker
                selected={endDate || undefined}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                minDate={startDate || undefined}
                maxDate={new Date()}
                dateFormat="dd.MM.yyyy"
                placeholderText="Конец"
                className={styles.datePicker}
                isClearable
              />
            </div>
            {hasActiveFilters && (
              <button
                className={styles.clearButton}
                onClick={handleClearFilters}
              >
                Сбросить
              </button>
            )}
          </div>
        </div>
      )}

      <div className={styles.logsContainer}>
        {isLoading || isActionsLoading ? (
          <div className={styles.loader}>
            <Preloader />
          </div>
        ) : isActionsError ? (
          <div className={styles.error}>
            Ошибка при загрузке логов пользователя.
          </div>
        ) : filteredLogs.length > 0 ? (
          <>
            {hasActiveFilters && (
              <div className={styles.resultsInfo}>
                Найдено: {filteredLogs.length} из {actionsData?.actions?.length || 0}
              </div>
            )}
            <div className={styles.logsList}>
              {filteredLogs.map((action: any, index: number) => (
                <div key={action.id || index} className={styles.logItem}>
                  <span className={styles.logDate}>
                    {action.of_date ? formatScheduleDate(action.of_date, true) : "—"}
                  </span>
                  <span className={styles.logAction}>{action.action}</span>
                </div>
              ))}
            </div>
          </>
        ) : hasActiveFilters ? (
          <div className={styles.empty}>
            Логи не найдены по заданным фильтрам.
          </div>
        ) : (
          <div className={styles.empty}>
            Логи пользователя отсутствуют.
          </div>
        )}
      </div>
    </div>
  );
};
